/**
 * Auth request schemas now live in the single source of truth
 * (`@imobdesk/contracts`). This file re-exports them under Form/Request names
 * the app already uses so existing imports keep working.
 *
 * Response schemas (`loginSuccessResponseSchema`, etc.) also live in the
 * contract — import from `@imobdesk/contracts` auth namespace directly in
 * actions (see `auth.actions.ts`).
 *
 * NOTE: the contract login password rule is `min(1)` (backend authority),
 * relaxed from the previous frontend-only `min(6)` form rule.
 */
import { formPayload } from '@imobdesk/form-utils'
import { auth } from '@imobdesk/contracts'

// ==================== Request schemas (contrato — chamadas API) ====================

export const loginEmailPasswordRequestSchema = auth.loginRequestSchema
export const loginMagicLinkRequestSchema = auth.loginMagicLinkRequestSchema
export const recuperarSenhaRequestSchema = auth.newPasswordRequestSchema
export const abrirContaRequestSchema = auth.registerRequestSchema
export const validateOtpRequestSchema = auth.validateOtpRequestSchema
export const checkMagicLinkRequestSchema = auth.checkMagicLinkRequestSchema

// ==================== Form schemas (UX) ====================

export const loginEmailPasswordFormSchema = auth.loginRequestSchema
export type LoginEmailPasswordForm = auth.LoginRequest

export const loginMagicLinkFormSchema = auth.loginMagicLinkRequestSchema
export type LoginMagicLinkForm = auth.LoginMagicLinkRequest

export const recuperarSenhaFormSchema = auth.newPasswordRequestSchema
export type RecuperarSenhaForm = auth.NewPasswordRequest

export const abrirContaFormSchema = auth.registerRequestSchema
export type AbrirContaForm = auth.RegisterRequest

export const validateOtpFormSchema = auth.validateOtpRequestSchema
export type ValidateOtpForm = auth.ValidateOtpRequest

export function mapLoginEmailPasswordFormToApiRequest(
  input: LoginEmailPasswordForm,
): auth.LoginRequest {
  return loginEmailPasswordRequestSchema.parse(
    formPayload(input as Record<string, unknown>),
  )
}

export function mapLoginMagicLinkFormToApiRequest(
  input: LoginMagicLinkForm,
): auth.LoginMagicLinkRequest {
  return loginMagicLinkRequestSchema.parse(
    formPayload(input as Record<string, unknown>),
  )
}

export function mapRecuperarSenhaFormToApiRequest(
  input: RecuperarSenhaForm,
): auth.NewPasswordRequest {
  return recuperarSenhaRequestSchema.parse(
    formPayload(input as Record<string, unknown>),
  )
}

export function mapAbrirContaFormToApiRequest(
  input: AbrirContaForm,
): auth.RegisterRequest {
  return abrirContaRequestSchema.parse(
    formPayload(input as Record<string, unknown>),
  )
}

/** UX: formulário envia `code`; API de check-magic-link espera `token`. */
export function mapValidateOtpFormToApiRequest(
  input: ValidateOtpForm,
): auth.CheckMagicLinkRequest {
  const validated = validateOtpRequestSchema.parse(input)
  return checkMagicLinkRequestSchema.parse({
    token: validated.code,
    email: validated.email,
  })
}

export type LoginEmailPasswordRequest = auth.LoginRequest
export type LoginMagicLinkRequest = auth.LoginMagicLinkRequest
export type RecuperarSenhaRequest = auth.NewPasswordRequest
export type AbrirContaRequest = auth.RegisterRequest
export type ValidateOtpRequest = auth.ValidateOtpRequest

/** Re-exports de responses para callers que preferem importar via auth.schema */
export const loginSuccessResponseSchema = auth.loginSuccessResponseSchema
export type LoginSuccessResponse = auth.LoginSuccessResponse

export const refreshTokenResponseSchema = auth.refreshTokenResponseSchema
export type RefreshTokenResponse = auth.RefreshTokenResponse

export const recuperarSenhaResponseSchema = auth.recuperarSenhaResponseSchema
export type RecuperarSenhaResponse = auth.RecuperarSenhaResponse

export const abrirContaResponseSchema = auth.abrirContaResponseSchema
export type AbrirContaResponse = auth.AbrirContaResponse

export const magicLinkResponseSchema = auth.magicLinkResponseSchema
export type MagicLinkResponse = auth.MagicLinkResponse
